import { InviteStatus, canEditPermissions } from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../../elements/alert-dialog";
import "../../../elements/scroller";
import { app } from "../../../init";
import { singleton } from "../../../lib/singleton";
import { Routing, routeProperty } from "../../../mixins/routing";
import { StateMixin } from "../../../mixins/state";
import { shared } from "../../../styles";
import "../components/permissions-form";
import type { PermissionsForm } from "../components/permissions-form";
import "./components/invite-dialog-single";
import type { InviteDialogSingle } from "./components/invite-dialog-single";

@customElement("ptc-employees-permissions-single")
export class EmployeesPermissionsSingle extends Routing(StateMixin(LitElement)) {
    routePattern = /^employees\/(?<id>\d+)\/permissions/;

    get routeTitle() {
        return `Zugänge & Rechte: ${this._employee && this._employee.name}`;
    }

    get _hasChanged() {
        return this._form?.hasChanged;
    }

    @query("ptc-permissions-form")
    private _form: PermissionsForm;

    @routeProperty({ arg: "id", type: Number })
    private _employeeId: number;

    @state()
    private _loading = false;

    @singleton("ptc-invite-dialog-single")
    private _inviteDialog: InviteDialogSingle;

    private get _employee() {
        return (this._employeeId && app.getEmployee(this._employeeId)) || null;
    }

    private async _sendInvite() {
        const sent = await this._inviteDialog.show(this._employee!);
        if (sent) {
            // update in the background
            void app.fetchCompany();
        }
    }

    private async _submit() {
        this._loading = true;
        try {
            const { filters, permissions, ...rest } = this._form.data;
            await app.updateEmployee(
                this._employee!,
                {
                    ...rest,
                    access: {
                        filters,
                        permissions,
                    },
                },
                true
            );
            this.requestUpdate("_employeeId");
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _revokeAccess() {
        if (
            !(await confirm(
                "Sind Sie Sicher dass Sie dem Mitarbeiter den Zugang zu Ihrem Unternehmen entziehen wollen?",
                "Zugang Entziehen",
                "Abbrechen",
                { title: "Zugang Entziehen", type: "destructive", icon: "icon.status.blocked" }
            ))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.updateEmployee(this._employee!, { revokeAccess: true }, true);
            this.requestUpdate("_employeeId");
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    updated(changes: Map<string, unknown>) {
        if ((!changes.has("_employeeId") && !changes.has("active")) || !this._form || !this._employee) {
            return;
        }

        void this._resetPermissions();
    }

    private async _resetPermissions() {
        this._form.filters = this._employee?.access?.filters || [];
        this._form.permissions = this._employee?.access?.permissions || [];
        this._form.accessRole = this._employee!.role;
        this._form.requestUpdate("filters");
        this._form.requestUpdate("permissions");
        this._form.requestUpdate("accessRole");
        await this._form.updateComplete;
        this.requestUpdate();
    }

    static styles = [shared, css``];

    render() {
        if (!this._employee) {
            return;
        }
        const emp = this._employee;
        return html`
            ${!!emp.accountId || emp.invite?.status === "sent"
                ? html`
                      <div class="fullbleed vertical layout">
                          ${!emp.invite
                              ? ""
                              : emp.invite.status === InviteStatus.DeliveryFailed
                                ? html`
                                      <div class="margined red padded box spacing start-aligning horizontal layout">
                                          <ptc-ui-icon name="icon.status.warning" class="big"></ptc-ui-icon>
                                          <div class="stretch">
                                              Die an die Emailadresse <strong>${emp.email}</strong> versandte Einladung
                                              konnte leider nicht zugestellt werden. Bitte überprüfen Sie die
                                              Emailadresse des Mitarbeiters und versuchen Sie es erneut.
                                          </div>
                                          <button class="small subtle" @click=${this._sendInvite}>
                                              <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Erneut
                                              Versenden
                                          </button>
                                      </div>
                                  `
                                : emp.invite.status === InviteStatus.Created || emp.invite.status === InviteStatus.Sent
                                  ? html`
                                        <div
                                            class="margined orange padded box spacing start-aligning horizontal layout"
                                        >
                                            <ptc-ui-icon name="icon.action.send-message" class="big"></ptc-ui-icon>
                                            <div class="stretch">
                                                Es wurde eine Einladung an die Emailadresse
                                                <strong>${emp.email}</strong> versandt. Sie können die Berechtigungen
                                                des Mitarbeiters bereits jetzt bearbeiten - Ihre Einstellungen finden
                                                Anwendung sobald der Mitarbeiter seinen Zugang eingerichtet hat.
                                            </div>
                                            <button class="small subtle" @click=${this._sendInvite}>
                                                <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Erneut
                                                Versenden
                                            </button>
                                        </div>
                                    `
                                  : ""}

                          <ptc-permissions-form
                              class="stretch"
                              .grantingProfile=${app.profile}
                              ?readonly=${!canEditPermissions(this._employee, app.profile?.id)}
                              @change=${() => this.requestUpdate()}
                              @revoke-access=${this._revokeAccess}
                          ></ptc-permissions-form>

                          <div>
                              <div
                                  class="horizontal padded evenly stretching spacing layout narrow-content"
                                  ?hidden=${!this._hasChanged}
                              >
                                  <button class="primary" @click=${this._submit}>Speichern</button>
                                  <button class="transparent" type="button" @click=${this._resetPermissions}>
                                      Abbrechen
                                  </button>
                              </div>
                          </div>
                      </div>
                  `
                : html`
                      <div class="fullbleed vertical centering layout">
                          <div class="double-margined">Dieser Mitarbeiter hat noch keinen Pentacode-Account.</div>
                          <button class="primary" @click=${this._sendInvite}>
                              <ptc-ui-icon name="icon.action.send-message"></ptc-ui-icon> Einladung Versenden
                          </button>
                      </div>
                  `}

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
